import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { DynamicForm } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import { parseAdditionalWidgetOptions } from 'dynamic-form/util/parse-additional-widget-options';
import { BulkOperationType } from 'listings/enums/bulk-operation-type.enum';
import { ContentScoringResult } from 'listings/interfaces/content-scoring';
import { CustomField } from 'listings/interfaces/custom-field';
import { DuplicateListing } from 'listings/interfaces/duplicate-listing';
import { Listing } from 'listings/interfaces/listing.interface';
import { Observable, catchError, combineLatest, debounceTime, finalize, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { ListingStatusBEEnum, ListingStatusEnum, listingStatusMap } from 'shared/constants/listing-statuses';
import { RESTRICTIVE_UI_LISTINGS } from 'shared/constants/storage-keys';
import { DistributionCategory } from 'shared/enums/distribution-types.enum';
import { generateHttpParams } from 'shared/functions/generate-http-params.function';
import { BYPASS_ERROR_HANDLER_FOR_ERROR_CODES } from 'shared/interceptors/error.interceptor';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { FeatureGroup } from 'shared/interfaces/feature-group.interface';
import { ListingType } from 'shared/interfaces/listing-type.interface';
import { CachingService, EventBusService, StorageService, ToastService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { waitForTranslationLoaded } from 'shared/util/wait-translations-loaded';
import { ValidationError } from '../interfaces/validation-error';

const LISTING_API = environment.BASE_URL + 'listingApi/';
const USER_API = environment.BASE_URL + 'userApi/';

const urls = {
    values: {
        base: 'values',
        version: 'values/version',
        listingVersions: 'values/listing-versions',
        changedFeatureGroups: 'values/changed-feature-groups',
        createListing: 'values/createListing',
        duplicateListing: 'values/duplicate-listing',
        validationErrors: 'values/validation-errors',
        createDraftListing: 'values/create-draft',
        dropDraftListing: 'values/drop-draft-listing',
        rejectListing: 'values/reject-document',
        updateField: 'values/updateField',
        createTableField: 'values/createDocument',
        submitListing: 'values/submit-listing',
        updateTableField: 'values/updateDocument',
        deleteTableField: 'values/deleteDocument',
        outletsData: 'values/current-outlets-data',
        bookingAuthorizationStatus: 'values/booking-authorization-status',
        deleteAndApplyRoomAmenitiesSet: 'values/deleteAndApplyRoomAmenitiesSet',
        bulkEditValues: 'values/bulk-values',
        contentScore: 'values/content-score-summary',
    },
    settings: {
        base: 'settings',
        availableFeatureGroups: 'settings/featureGroups',
        sections: 'settings/sections',
        widgets: 'settings/widgets',
        addSupplierField: 'settings/add-supplier-field',
        resources: 'settings/resources',
        listingTypes: 'settings/listingTypes',
        createListingStructure: 'settings/getCreateListingFormDetails',
        bulkEditFeatureGroups: 'settings/bulk-get-edit-feature-groups',
        bulkEditSettings: 'settings/bulk-edit-settings',
        channelRestrictedSettings: 'settings/channel-restricted-settings',
    },
};

@Injectable({
    providedIn: 'root',
})
export class ListingsService {
    private requestCache: Map<string, Observable<any>> = new Map();
    private apollo: ApolloBase;

    constructor(
        private http: HttpClient,
        private apolloProvider: Apollo,
        private translate: TranslateService,
        private cachingService: CachingService,
        private storageService: StorageService,
        private toastService: ToastService,
        private eventBusService: EventBusService,
        private featureFlagService: FeatureFlagService,
        private router: Router,
    ) {
        this.apollo = this.apolloProvider.use('listingAPI');
    }

    private inflightOutletDataRequests: { [key: string]: Observable<any> } = {};

    validationIssues = signal<ValidationError | null>(null);
    currentListingFeatureGroupsList = signal<FeatureGroup[]>([]);
    currentListingFeatureGroupValues = signal<{
        featureId: string;
        fieldName: string;
        values: {
            [key: string]: any;
        };
    } | null>(null);

    public setValuesForCurrentListingFeatureGroup(
        { featureId, fieldName }: { featureId: any; fieldName: any },
        values: { [key: string]: any },
    ) {
        this.currentListingFeatureGroupValues.set({ featureId, fieldName, values });
    }

    currentOutletFeatureGroupsList = signal<FeatureGroup[]>([]);
    currentOutletFeatureGroupValues = signal<{
        featureId: string;
        fieldName: string;
        values: {
            [key: string]: any;
        };
    } | null>(null);

    currentListing = signal<Listing | null>(null);

    public setValuesForCurrentOutletFeatureGroup(
        { featureId, fieldName }: { featureId: string; fieldName: string },
        values: { [key: string]: any },
    ) {
        // All "top" level object keys inside values are expected to be of the same list/enum as the fieldName (both here and for listing)
        // However, for outlets, there is one different case, where the "outlet" fieldName is received instead of the "outletProfile" one.
        // To avoid the issue where the pathing matching won't work between outlet and outletProfile, we adjust it here:
        const updatedFieldName: string = fieldName === 'outlet' ? 'outletProfile' : fieldName;
        const updatedValues = fieldName === 'outletProfile' && values?.outlet ? { ...values, outletProfile: values.outlet } : values;
        this.currentOutletFeatureGroupValues.set({ featureId, fieldName: updatedFieldName, values: updatedValues });
    }

    // Get general form structure from the back-end
    public getCreateListingFormStructure(): Observable<DynamicForm> {
        return this.http.get(LISTING_API + urls.settings.createListingStructure) as Observable<DynamicForm>;
    }

    public createListing(listingData: { [key: string]: any }) {
        return this.http.post(LISTING_API + urls.values.createListing, listingData);
    }

    public getBookingQuickConnectAuthorizationStatus(listingId: string, mappedValue: string): Observable<any> {
        if (!environment.dev) {
            return of(false);
        }

        return this.http.get(LISTING_API + urls.values.bookingAuthorizationStatus, {
            params: {
                listingId: listingId,
                mappedId: mappedValue,
            },
        });
    }

    public getListingById(id: string): Observable<any> {
        // Check if there's an ongoing request for the same ID
        if (this.requestCache.has(id)) {
            return this.requestCache.get(id)!;
        }

        // Create the observable with debounceTime
        const observable = of(id).pipe(
            debounceTime(5),
            switchMap((searchId) => this.fetchListing(searchId)),
            shareReplay(1), // This will ensure that if the observable is subscribed to more than once, the result is shared.
        );

        // Store the ongoing request in the cache
        this.requestCache.set(id, observable);

        // Remove the request from the cache once it's completed
        observable.subscribe({
            complete: () => this.requestCache.delete(id),
            error: () => this.requestCache.delete(id),
        });

        return observable;
    }

    public getBulkFeatureGroups(bulkOperationId: string): Observable<any> {
        return this.cachingService
            .wrapCache(
                'getListingsByBulkIds',
                () => {
                    return this.http.get<FeatureGroup[]>(LISTING_API + urls.settings.bulkEditFeatureGroups, {
                        params: { bulkOperationId },
                    });
                },
                [bulkOperationId],
            )
            .pipe(
                map((featureGroups: FeatureGroup[]) => {
                    featureGroups = featureGroups.map((data: FeatureGroup) => {
                        data.name = this.translate.instant(`listing.featureGroups.${data.fieldName}`);
                        return data;
                    });

                    return featureGroups.orderBy('name');
                }),
            );
    }

    public getContentScoringForListing(listingId: string): Observable<ContentScoringResult | null> {
        if (!listingId) {
            return of(null);
        }

        return this.http.get<ContentScoringResult>(LISTING_API + urls.values.contentScore, {
            params: { listingId },
        });
    }

    public getBulkFormStructure(bulkOperationId: string, featureGroupId: any): Observable<DynamicForm> {
        return (
            this.http.get(LISTING_API + urls.settings.bulkEditSettings, {
                params: { bulkOperationId, featureGroupId },
            }) as Observable<any> as Observable<DynamicForm>
        ).pipe(
            map((dynamicForm: DynamicForm) => {
                parseAdditionalWidgetOptions(dynamicForm.inputs);
                return dynamicForm;
            }),
        );
    }

    public getBulkFormValues(bulkOperationId: string, featureGroupId: any): Observable<any> {
        return this.http.get(LISTING_API + urls.values.bulkEditValues, { params: generateHttpParams({ bulkOperationId, featureGroupId }) });
    }

    private fetchListing(id: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query search {
                    listings {
                        searchListings(filtering: {
                            searchTerm: "${id}"
                        }) {
                            data {
                                id
                                name
                                city
                                countryId
                                exportsToIcePortal
                                shijiId
                                listingStatus
                                addressValidationStatus
                                contentDistributionCategory
                                visualsDistributionCategory
                                distributionSettings {
                                    distributionRestrictions {
                                        channelId
                                    }
                                    distributionCategory
                                    restrictionType
                                }
                                brandName
                                brandId
                                masterListingId
                                supplierLogo
                                maxVersion
                                isReadOnly
                                hasVisualsAccess
                            }
                            totalCount
                            statusCode
                            message
                        }
                    }
                }
            `,
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListings.data[0] ?? {};
                }),
                tap((listing: Listing) => {
                    this.currentListing.set(listing);
                }),
            );
    }

    /**
     * Get the form structure (dynamic form) for any listing and any feature group for that listing
     * if no restrictive UI user preference is set for the listing
     * @param listingId
     * @param featureGroupId
     */
    public getFormStructure(listingId: string, featureGroupId: string): Observable<DynamicForm> {
        const restrictiveUIForListing: { [key: string]: string } = JSON.parse(
            (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
        );
        const currentUrl = this.router.url;
        if (
            restrictiveUIForListing?.[listingId] &&
            this.featureFlagService.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI] &&
            // ↓ Only applies restrictive UI when on a listing's page (e.g. not including the search listing page,
            // where the duplicate listing flow uses the same /settings call)
            currentUrl.includes('details') &&
            !currentUrl.includes('details/visuals')
        ) {
            return this.getChannelRestrictiveFormStructure(listingId, featureGroupId, restrictiveUIForListing[listingId]);
        }

        return this.cachingService.wrapCache(
            'getFormStructure',
            (listingIdParam: string, featureGroupIdParam: string) => {
                return (
                    this.http.get(LISTING_API + urls.settings.base, {
                        params: {
                            listingId: listingIdParam,
                            featureGroupId: featureGroupIdParam,
                        },
                    }) as Observable<DynamicForm>
                ).pipe(
                    map((dynamicForm: DynamicForm) => {
                        parseAdditionalWidgetOptions(dynamicForm.inputs);
                        return dynamicForm;
                    }),
                );
            },
            [listingId, featureGroupId],
            { ttl: 1000 },
        );
    }

    /**
     * Get the form values for any listing and any feature group for that listing
     * @param listingId
     * @param featureGroupId
     */
    public getFormValues(listingId: string, featureGroupId: string): Observable<any> {
        return this.cachingService.wrapCache(
            'getFormValues',
            (listingIdParam: string, featureGroupIdParam: string) => {
                return this.http
                    .get(LISTING_API + urls.values.base, {
                        params: {
                            featureGroupId: featureGroupIdParam,
                            listingId: listingIdParam,
                        },
                    })
                    .pipe(
                        tap((values) => {
                            this.setValuesForCurrentListingFeatureGroup(
                                { featureId: featureGroupIdParam, fieldName: Object.keys(values)[0] ?? '' },
                                values,
                            );
                        }),
                    ) as Observable<any>;
            },
            [listingId, featureGroupId],
            { ttl: 1000 },
        );
    }

    /**
     * Based on the listing's selected restrictive UI channel, get the form structure (dynamic form) for said listing and any feature group for that listing.
     * @param listingId
     * @param featureGroupId
     *
     * @param channelId - The channel id set on the listing's restrictive UI channel
     */
    public getChannelRestrictiveFormStructure(listingId: string, featureGroupId: string, channelId: string): Observable<DynamicForm> {
        return (
            this.http.get(LISTING_API + urls.settings.channelRestrictedSettings, {
                params: {
                    listingId: listingId,
                    featureGroupId: featureGroupId,
                    channelId: channelId,
                },
                context: new HttpContext().set(BYPASS_ERROR_HANDLER_FOR_ERROR_CODES, [409]),
            }) as Observable<any> as Observable<DynamicForm>
        ).pipe(
            map((dynamicForm: DynamicForm) => {
                parseAdditionalWidgetOptions(dynamicForm.inputs);
                return dynamicForm;
            }),
            catchError((err) => {
                // This error is returned when the channel saved by the user in local storage is no longer
                // available to be chosen from the listing's restrictive UI channels list.
                // Because if this, we need to remove it from the local storage as well,
                // as it can't be used to restrict the UI anymore
                if (err.status === 409) {
                    const message = this.translate.instant('listings.userPreferences.restrictiveUIChannelNotAvailable');
                    this.toastService.showToast(message, 'error');

                    const currentRestrictiveUIPreferencesFromStorageParsed = JSON.parse(
                        (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
                    );

                    if (currentRestrictiveUIPreferencesFromStorageParsed[listingId]) {
                        delete currentRestrictiveUIPreferencesFromStorageParsed[listingId];
                        this.storageService.setInLocal(
                            RESTRICTIVE_UI_LISTINGS,
                            JSON.stringify(currentRestrictiveUIPreferencesFromStorageParsed),
                        );
                        this.eventBusService.dispatch('listing:userPreferences:restrictiveUiChannelsUpdate', true);
                        return this.getFormStructure(listingId, featureGroupId);
                    }
                }
                return of(err);
            }),
        );
    }

    public createTableValues(listingId: string, changedData: any): Observable<any> {
        return this.http.post(`${LISTING_API}${urls.values.createTableField}`, changedData, {
            params: {
                listingId: listingId,
            },
        }) as Observable<any>;
    }

    public updateTableValues(listingId: string, changedData: any): Observable<any> {
        const paramsObject: { params: { listingId: string; updateFromRestrictedView?: boolean } } = {
            params: {
                listingId: listingId,
            },
        };
        const restrictiveUIForListing: { [key: string]: string } = JSON.parse(
            (this.storageService.getFromLocal(RESTRICTIVE_UI_LISTINGS) as string) || '{}',
        );
        if (restrictiveUIForListing?.[listingId] && this.featureFlagService.featureFlags()[FeatureFlagEnum.RESTRICTIVE_UI]) {
            paramsObject.params.updateFromRestrictedView = true;
        }

        return this.http.put(`${LISTING_API}${urls.values.updateTableField}`, changedData, paramsObject) as Observable<any>;
    }

    public deleteTableValues(listingId: string, changedData: any): Observable<any> {
        return this.http.delete(`${LISTING_API}${urls.values.deleteTableField}`, {
            body: changedData,
            params: {
                listingId: listingId,
            },
        }) as Observable<any>;
    }

    public deleteAndApplyRoomAmenitiesSet(listingId: string, changedData: any): Observable<any> {
        return this.http.delete(`${LISTING_API}${urls.values.deleteAndApplyRoomAmenitiesSet}`, {
            body: changedData,
            params: {
                listingId: listingId,
            },
        }) as Observable<any>;
    }

    public updateValues(listingId: string, changedData: any): Observable<any> {
        return this.http.post(`${LISTING_API}${urls.values.updateField}`, changedData, {
            params: {
                listingId: listingId,
            },
        }) as Observable<any>;
    }

    public createDraftListing(listingId: string): Observable<string> {
        return this.http.post(
            `${LISTING_API}${urls.values.createDraftListing}`,
            {},
            {
                params: { listingId: listingId },
                responseType: 'text',
            },
        ) as Observable<string>;
    }

    public deleteListing(listingId: string) {
        return this.http.delete(`${LISTING_API}${urls.values.base}/${listingId}`, { responseType: 'text' }) as Observable<string>;
    }

    /**
     * If called by a shiji user:
     * - the call can have no params which returns all the featureGroups existing in the application
     * - OR with the supplierId param which will return all the featureGroups available for the specified supplier
     *
     * If called by a supplier user:
     * - the call needs to have at least 1 param and returns all the featureGroups the user has access to,
     * based on their User Group Permissions OR Individual Permissions and combining it with the listingId, outletId and outletType
     * @param listingId
     * @param outletId
     * @param outletType
     * @param supplierId
     */
    public getFeatureGroups(listingId?: string, outletId?: string, outletType?: number, supplierId?: string): Observable<FeatureGroup[]> {
        const params = generateHttpParams({ listingId, outletId, outletType, supplierId });

        return this.http.get<FeatureGroup[]>(`${LISTING_API}${urls.settings.availableFeatureGroups}`, { params }).pipe(
            //TODO: rethink this approach
            switchMap((featureGroups) => combineLatest([waitForTranslationLoaded(this.translate), of(featureGroups)])),
            map(([translationLoaded, featureGroups]) => {
                if (outletId) {
                    //We have outletProfile and outletDetails instead of listingProfile and listingDetails,
                    //but they're not treated as different featureGroups.
                    //outletProfile is received as 'outlet' so we look for that and transform it here
                    const outletProfile = featureGroups.find((fg) => fg.fieldName === 'outlet');
                    const outletDetails = featureGroups.find((fg) => fg.fieldName === 'listingDetails');

                    if (outletProfile) {
                        outletProfile.fieldName = 'outletProfile';
                        outletProfile.name = 'Outlet Profile';
                    }

                    if (outletDetails) {
                        outletDetails.fieldName = 'outletDetails';
                        outletDetails.name = 'Outlet Details';
                    }
                }

                featureGroups = featureGroups.map((data) => {
                    data.name = this.translate.instant(`listing.featureGroups.${data.fieldName}`);
                    return data;
                });

                if (featureGroups.findIndex((fg) => fg.fieldName === 'listingProfile') !== -1) {
                    const listingProfile = featureGroups.find((fg) => fg.fieldName === 'listingProfile')!;
                    featureGroups = featureGroups.filter((fg) => fg !== listingProfile).orderBy('name');
                    featureGroups.unshift(listingProfile);
                } else if (featureGroups.findIndex((fg) => fg.fieldName === 'outletProfile') !== -1) {
                    const outletProfile = featureGroups.find((fg) => fg.fieldName === 'outletProfile')!;
                    featureGroups = featureGroups.filter((fg) => fg !== outletProfile).orderBy('name');
                    featureGroups.unshift(outletProfile);
                } else {
                    featureGroups = featureGroups.orderBy('name');
                }

                if (outletId) {
                    this.currentOutletFeatureGroupsList.set(featureGroups);
                } else {
                    this.currentListingFeatureGroupsList.set(featureGroups);
                }
                return featureGroups;
            }),
            catchError(() => {
                return of([
                    {
                        id: null,
                        name: null,
                    },
                ]) as Observable<FeatureGroup[]>;
            }),
        );
    }

    public getSections(featureGroupId: string, outletTypes?: number[]): Observable<any> {
        let requestUrl = `${LISTING_API}${urls.settings.sections}?featureGroupId=${featureGroupId}`;
        outletTypes?.forEach((outletType) => {
            requestUrl = requestUrl.concat(`&outletTypes=${outletType}`);
        });

        return this.http.get(requestUrl);
    }

    public getWidgets(): Observable<any> {
        return this.http.get(LISTING_API + urls.settings.widgets);
    }

    public addCustomField(customField: CustomField, supplierId?: string): Observable<any> {
        let params = new HttpParams();
        if (supplierId) {
            params = params.append('supplierId', supplierId);
        }

        return this.http.post(LISTING_API + urls.settings.addSupplierField, customField, {
            params: params,
        }) as Observable<any>;
    }

    /**
     * Get the available/visible feature groups for the provided supplier
     * taking into consideration what's visible for the combination of:
     * - the specific listingId (based on the brand it belongs to),
     * - the specific outletId
     * - the outlet's type
     * @param supplierId
     * @param listingId
     * @param outletId
     * @param outletType
     */
    public getFeatureGroupsResources(supplierId: string, listingId?: string, outletId?: string, outletType?: number) {
        const params = generateHttpParams({ supplierId, listingId, outletId, outletType });

        return this.http.get<FeatureGroup[]>(`${LISTING_API}${urls.settings.resources}`, { params }).pipe(
            catchError(() => {
                return of([
                    {
                        id: null,
                        name: null,
                    },
                ]) as Observable<FeatureGroup[]>;
            }),
        );
    }

    public fetchListingTypes(): Observable<ListingType[]> {
        return this.http.get<ListingType[]>(`${LISTING_API}${urls.settings.listingTypes}`).pipe(
            catchError(() => {
                return of([
                    {
                        id: -1,
                        name: String.EMPTY,
                    },
                ]) as Observable<ListingType[]>;
            }),
        );
    }

    public submitListing(listingId: string): Observable<any> {
        return this.http.post(
            LISTING_API + urls.values.submitListing,
            {},
            {
                params: {
                    listingId: listingId,
                },
            },
        ) as Observable<any>;
    }

    public rejectListing(listingId: string, reasonMessage: string): Observable<any> {
        return this.http.post(LISTING_API + urls.values.rejectListing, `"${reasonMessage}"`, {
            params: {
                listingId: listingId,
            },
            headers: {
                'Content-Type': 'application/json',
            },
        }) as Observable<any>;
    }

    public dropDraftListing(listingId: string): Observable<any> {
        return this.http.post(
            LISTING_API + urls.values.dropDraftListing,
            {},
            {
                params: {
                    listingId: listingId,
                },
            },
        ) as Observable<any>;
    }

    public getChannelListingMappings(listingId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`query searchChannelListingMappings {
                    listingMappings{
                        searchChannelListingMappings(listingId: "${listingId}")
                        {
                            data
                            {
                                channelId
                                channelName
                                isBrandDefault
                                isDefault
                                mappingType
                                mappingTypeDescription
                                channelListingName
                                channelMappedId
                                authorizationStatus
                            }
                            statusCode
                            message
                            totalCount
                        }
                    }
                }`,
            })
            .pipe(
                map((response: any) => {
                    return response?.data?.listingMappings?.searchChannelListingMappings?.data;
                }),
            );
    }

    public createListingMapping(listingMapping: any): Observable<any> {
        const createListingMapping = `mutation createListingMapping {
                listingMappings{
                    createListingMappingType(createListingMappingType: {
                        listingId: "${listingMapping.listingId}",
                        mappedType: ${listingMapping.mappedType},
                        mappedValue: "${listingMapping.mappedValue}",
                        ${listingMapping.mappedListingName ? 'mappedListingName: "' + listingMapping.mappedListingName + '"' : ''}
                    }) {
                        data {
                            id
                        }
                        statusCode
                        message
                    }
                }
            }`;

        return this.apollo.mutate({
            mutation: gql(createListingMapping),
        });
    }

    public updateListingMapping(listingMapping: any): Observable<any> {
        const updateListingMapping = `mutation updateListingMapping {
                    listingMappings{
                        updateListingMapping(updateListingMapping: {
                            listingId: "${listingMapping.listingId}",
                            mappedType: ${listingMapping.mappedType},
                            ${
                                listingMapping.mappedListingName !== null && listingMapping.mappedListingName !== undefined
                                    ? `mappedListingName: "${listingMapping.mappedListingName}"`
                                    : ''
                            }
                            ${
                                listingMapping.mappedValue !== null && listingMapping.mappedValue !== undefined
                                    ? `mappedValue: "${listingMapping.mappedValue}"`
                                    : ''
                            }
                            ${
                                listingMapping.authorizationStatus !== null && listingMapping.authorizationStatus !== undefined
                                    ? `authorizationStatus: ${listingMapping.authorizationStatus}`
                                    : ''
                            }
                        }) {
                            data {
                                id
                            }
                            statusCode
                            message
                        }
                    }
                }`;

        return this.apollo.mutate({
            mutation: gql(updateListingMapping),
        });
    }

    public removeListingMapping(listingMapping: any): Observable<any> {
        const removeListingMapping = `mutation removeListingMapping {
                listingMappings{
                    removeListingMapping(
                        listingId: "${listingMapping.listingId}",
                        mappingType: ${listingMapping.mappedType}
                    ) {
                        data {
                            id
                        }
                        statusCode
                        message
                    }
                }
            }`;

        return this.apollo.mutate({
            mutation: gql(removeListingMapping),
        });
    }

    public getSupplierListingMappings(listingId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`query searchSupplierListingMappings {
                listingMappings{
                    searchSupplierListingMappings(listingId: "${listingId}")
                    {
                        data
                        {
                            supplierId
                            supplierName
                            mappingType
                            mappingTypeDescription
                            supplierMappedId

                        }
                        statusCode
                        message
                        totalCount
                    }
                }
            }`,
            })
            .pipe(
                map((response: any) => {
                    return response.data.listingMappings.searchSupplierListingMappings.data;
                }),
            );
    }

    public updateListingContentDistribution(
        listingId: string,
        distributionCategory: DistributionCategory,
        distributionSettingsInput: string[],
    ): Observable<any> {
        const updateListingDistribution = `
            mutation updateListingDistribution {
                listings{
                    updateListing(updateListing: {
                        id: "${listingId}",
                        contentDistributionCategory: ${distributionCategory},
                        ${
                            distributionSettingsInput?.length
                                ? 'contentDistributionRestrictionsInput: [' +
                                  distributionSettingsInput.map((id: string) => `{channelId: "${id}"}`) +
                                  ']'
                                : ''
                        },
                    }) {
                        data
                        {
                            shijiId
                        }
                        statusCode
                        message
                    }
                }
            }`;

        return this.apollo.mutate({
            mutation: gql(updateListingDistribution),
        });
    }

    public getListingValidationErrors(listingId: string, forVisuals: boolean = false): Observable<any> {
        let params = new HttpParams();
        if (listingId) {
            params = params.append('listingId', listingId);
            if (forVisuals) {
                params = params.append('forVisuals', forVisuals);
            }
        }

        return this.http.get(LISTING_API + urls.values.validationErrors, { params: params }) as Observable<any>;
    }

    public duplicateListing(shijiId: string, body: DuplicateListing): Observable<any> {
        let params = new HttpParams();
        if (shijiId) {
            params = params.append('shijiId', shijiId);
        }

        return this.http.post(LISTING_API + urls.values.duplicateListing, body, { params: params }) as Observable<any>;
    }

    public getListingByIdAndVersion(id: string, value: string | number) {
        if ((!id || !value) && !environment.production) {
            new ReferenceError('Id or Value is not defined.');
        }

        let params = new HttpParams();
        params = params.append('listingId', id);
        params = params.append('version', value);

        return this.http.get(`${LISTING_API}${urls.values.version}`, { params: params });
    }

    public getListingVersions(listingId: string): Observable<{ version: number; label: string }[]> {
        if (!listingId && !environment.production) {
            new ReferenceError('ListingId is not defined.');
        }

        let params = new HttpParams();
        params = params.append('listingId', listingId);

        return this.http.get<{ version: number; label: string }[]>(`${LISTING_API}${urls.values.listingVersions}`, { params: params });
    }

    /**Get the list of modified feature group names for either a listing, or an outlet.
     *
     * NEVER provide BOTH a listingId AND an outletId!
     * If both values are provided, this function will return BY DEFAULT the modified feature group names for the listing.
     * @param {number} versionNumber - required
     * @param {string} listingId - if provided, will return the modified feature groups of the listing
     * @param {string} outletId - if provided, will return the modified feature groups of the outlet
     */
    public getChangedFeatureGroups(versionNumber: number, listingId?: string, outletId?: string) {
        if ((!versionNumber || isNaN(Number(versionNumber))) && !environment.production) {
            new ReferenceError('VersionNumber is either empty or not a number.');
        } else if (!listingId && !outletId && !environment.production) {
            new ReferenceError('ListingId OR OutletId must be provided.');
        }

        let params = new HttpParams();
        params = params.append('version', versionNumber);

        if (listingId) {
            params = params.append('listingId', listingId);
        } else if (outletId) {
            params = params.append('outletId', outletId);
        }

        return this.http.get(`${LISTING_API}${urls.values.changedFeatureGroups}`, { params: params });
    }

    public getOutletFormComparisonData(listingId: string): Observable<any> {
        const cacheKey = `outletData_${listingId}`;

        // If an inflight request exists, return it
        if (this.inflightOutletDataRequests[cacheKey]) {
            return this.inflightOutletDataRequests[cacheKey];
        }

        // Create the request and store it
        const request$ = this.http
            .get(LISTING_API + urls.values.outletsData, {
                params: {
                    listingId: listingId,
                },
            })
            .pipe(
                // Ensures all subscribers get the same result
                shareReplay(1),
                // Clean up once completed
                finalize(() => {
                    delete this.inflightOutletDataRequests[cacheKey];
                }),
            );

        this.inflightOutletDataRequests[cacheKey] = request$;

        return request$;
    }

    public getAllListingIds(
        totalRowCount: number,
        filterModel: { [key: string]: any },
        options?: { takeMargin: boolean },
    ): Observable<any> {
        const variables: any = {
            companyName: filterModel['companyName']?.filter,
            name: filterModel['name']?.filter,
            city: filterModel['city']?.filter,
            countryName: filterModel['countryName']?.filter,
            brandName: filterModel['brandName']?.filter,
            shijiId: filterModel['shijiId']?.filter,
            mappedId: filterModel['mappedId']?.filter,
            listingStatuses: filterModel['listingStatus']?.values,
        };
        const hasFilters = Object.keys(filterModel).length !== 0;

        if (variables.listingStatuses?.includes(ListingStatusEnum.NEW)) {
            //If we only filtered by "New/Never live" listings, we want all statuses AND maxVersion = 1
            if (variables.listingStatuses?.length === 1) {
                variables.version = 1;
                variables.listingStatuses = Object.values(ListingStatusEnum).filter(
                    (status) => status !== ListingStatusEnum.LIVE && status !== ListingStatusEnum.NEW,
                );
            } else {
                variables.listingStatuses = variables.listingStatuses.map((status: ListingStatusEnum) =>
                    status === ListingStatusEnum.NEW ? null : status,
                );
            }
        }

        const query = `query getListingsRowCount {
            listings {
                searchListings(searchQuery: {
                    pageNumber: 1,
                    pageSize: ${totalRowCount + (options?.takeMargin === false ? 0 : 10)},
                },
                    ${hasFilters ? 'filtering: {' : ''}
                        ${variables.companyName ? 'companyName: "' + variables.companyName + '"' : ''},
                        ${variables.name ? 'name: "' + variables.name + '"' : ''},
                        ${variables.city ? 'city: "' + variables.city + '"' : ''},
                        ${variables.countryName ? 'countryName: "' + variables.countryName + '"' : ''},
                        ${variables.brandName ? 'brandName: "' + variables.brandName + '"' : ''},
                        ${variables.shijiId ? 'shijiId: "' + variables.shijiId + '"' : ''},
                        ${variables.mappedId ? 'mappedId: "' + variables.mappedId + '"' : ''},
                        ${variables.listingStatuses ? 'listingStatuses: [' + variables.listingStatuses + ']' : ''},
                        ${variables.version ? 'version: ' + variables.version : ''},
                    ${hasFilters ? '}' : ''}) {
                    data {
                        id,
                        listingStatus
                    }
                    totalCount
                }
            }
        }`;

        return this.apollo
            .query({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListings;
                }),
            );
    }

    public publishListings(batchId: string): Observable<any> {
        return this.http.post(`${USER_API}bulkOperation/start-operation`, {
            bulkOperationId: batchId,
            operationType: BulkOperationType.PUBLISH,
        });
    }

    public deleteListings(batchId: string): Observable<any> {
        return this.http.post(`${USER_API}bulkOperation/start-operation`, {
            bulkOperationId: batchId,
            operationType: BulkOperationType.DELETE,
        });
    }

    public getListingBatchId(listingIds: string[]): Observable<string> {
        return this.http
            .post(`${USER_API}bulkOperation/create`, {
                operationInformation: listingIds.join(','),
            })
            .pipe(map((response: any) => response.id as string));
    }

    public saveBulkEditListing(batchId: string, values: any) {
        return this.http.post(`${USER_API}bulkOperation/start-operation`, {
            bulkOperationId: batchId,
            operationType: BulkOperationType.EDIT,
            dataToProcess: values,
        });
    }

    public getBulkListings(batchId: string): Observable<any> {
        return this.cachingService.wrapCache(
            'getBulkListings',
            (bulkOperationId: string) => {
                return this.http
                    .get<{
                        listings: Listing[];
                        supplierLogo: string;
                        totalCount: number;
                    }>(`${LISTING_API}values/get-bulk-listings-data`, { params: { bulkOperationId } })
                    .pipe(
                        map(({ listings, supplierLogo, totalCount }) => {
                            if (!Array.isArray(listings)) {
                                return [];
                            }

                            const processedListings = listings.map((listing: Listing) => {
                                listing.listingStatus = listingStatusMap[listing.listingStatus as unknown as ListingStatusBEEnum];

                                return listing;
                            });

                            return { listings: processedListings, supplierLogo, totalCount };
                        }),
                    );
            },
            [batchId],
        );
    }

    private getAllBulkEditValues(batchId: string): Observable<any> {
        return this.http
            .get(`${LISTING_API}values/bulk-all-values`, {
                params: {
                    bulkOperationId: batchId,
                },
            })
            .pipe(
                map((values) => {
                    this.eliminateNullsAndEmptyNodes(values);
                    return values;
                }),
            );
    }

    /**
     * Recursively eliminates null values and empty nodes from the given object. Mutates original object
     * @param {any} obj - The object to process.
     */
    private eliminateNullsAndEmptyNodes(obj: any) {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === null) {
                delete obj[key];
            } else if (typeof obj[key] === 'object') {
                this.eliminateNullsAndEmptyNodes(obj[key]);

                if (Object.keys(obj[key]).length === 0) {
                    delete obj[key];
                }
            }
        });
    }

    private getAllBulkEditSettings(batchId: string) {
        return this.http
            .get<DynamicForm>(`${LISTING_API}settings/bulk-edit-all-settings`, {
                params: {
                    bulkOperationId: batchId,
                },
            })
            .pipe(
                map((settings) => {
                    const clusteredSettings: { [key: string]: any } = {};

                    settings.inputs.forEach((input) => {
                        const inputLabelArray = input.label.split('.');

                        if (clusteredSettings[inputLabelArray[1]]) {
                            clusteredSettings[inputLabelArray[1]].push(input);
                        } else {
                            clusteredSettings[inputLabelArray[1]] = [input];
                        }
                    });

                    return clusteredSettings;
                }),
            );
    }

    public getBulkEditInitialData(batchId: string): Observable<any> {
        return combineLatest([this.getAllBulkEditSettings(batchId), this.getAllBulkEditValues(batchId)]).pipe(
            map(([settings, values]) => {
                return { settings, values };
            }),
        );
    }

    public searchListings(variables: any, pageNumber: number, pageSize: number, supplierId: string | undefined) {
        variables.orderDirection ??= 'ASC';
        if (variables.listingStatuses?.includes(ListingStatusEnum.NEW)) {
            //If we only filtered by "New/Never live" listings, we want all statuses AND maxVersion = 1
            if (variables.listingStatuses?.length === 1) {
                variables.version = 1;
                variables.listingStatuses = Object.values(ListingStatusEnum).filter(
                    (status) => status !== ListingStatusEnum.LIVE && status !== ListingStatusEnum.NEW,
                );
            } else {
                variables.listingStatuses = variables.listingStatuses.map((status: ListingStatusEnum) =>
                    status === ListingStatusEnum.NEW ? null : status,
                );
            }
        }

        const query = `query getListings {
                    listings {
                        searchListings(searchQuery: {
                            ${pageNumber ? 'pageNumber:' + pageNumber : ''},
                            ${pageSize ? 'pageSize:' + pageSize : ''},
                            ${variables.orderBy ? 'orderBy: "' + variables.orderBy + '"' : ''},
                            ${variables.orderDirection ? 'orderDirection:' + variables.orderDirection : ''}
                        },
                            filtering: {
                                ${variables.companyName ? 'companyName: "' + variables.companyName + '"' : ''},
                                ${variables.name ? 'name: "' + variables.name + '"' : ''},
                                ${variables.city ? 'city: "' + variables.city + '"' : ''},
                                ${variables.countryName ? 'countryName: "' + variables.countryName + '"' : ''},
                                ${variables.brandName ? 'brandName: "' + variables.brandName + '"' : ''},
                                ${variables.shijiId ? 'shijiId: "' + variables.shijiId + '"' : ''},
                                ${variables.mappedId ? 'mappedId: "' + variables.mappedId + '"' : ''},
                                ${variables.listingStatuses ? 'listingStatuses: [' + variables.listingStatuses + ']' : ''},
                                ${variables.version ? 'version: ' + variables.version : ''},
                            }) {
                            data {
                                id
                                ${!supplierId ? 'companyName' : ''}
                                ${!supplierId ? 'supplierId' : ''}
                                icePortalId
                                shijiId
                                name
                                mappedId
                                brandName
                                countryName
                                city
                                listingStatus
                                maxVersion
                                hasVisualsAccess
                                isReadOnly
                            }
                            totalCount
                        }
                    }
                }`;

        return this.apollo
            .query({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListings;
                }),
            );
    }

    public getListingsFromCompanyName(companyName: string) {
        const query = `query getListings {
                listings {
                    searchListings(searchQuery: {
                        pageNumber: 1,
                        pageSize: 10000,
                        orderBy: "name"
                        },
                        filtering: {
                            companyName: "${companyName}"
                    }) {
                        data {
                            id
                            shijiId
                            name
                        }
                    }
                }
            }`;

        return this.apollo
            .query({
                query: gql(query),
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.listings.searchListings.data;
                }),
            );
    }

    public enqueueForValidation(listingId: string): Observable<void> {
        return this.http.post<void>(`${LISTING_API}values/enqueue-for-validation?listingId=${listingId}`, null);
    }
}
